<script setup lang="ts">
  interface NavItem {
    label: string
    href?: string
    active?: boolean
  }

  withDefaults(defineProps<{ items?: NavItem[] }>(), { items: () => [] })

  defineEmits<{ navigate: [item: NavItem] }>()
</script>

<template>
  <header class="site-header">
    <div class="site-header__logo">
      <slot name="logo" />
    </div>
    <nav class="site-header__navigation">
      <a
        v-for="item in items"
        :key="item.label"
        :href="item.href || '#'"
        class="site-header__navigation-link"
        :aria-current="item.active ? 'page' : undefined"
        @click="$emit('navigate', item)"
      >
        {{ item.label }}
      </a>
    </nav>
    <div class="site-header__call-to-action">
      <slot name="cta" />
    </div>
  </header>
</template>

<style scoped lang="scss">
  .site-header {
    height: var(--bf-header-h-desktop);
    background: var(--bf-white);
    display: flex;
    align-items: center;
    padding: 0 32px;
    gap: 32px;
    border-bottom: 1px solid var(--bf-line);

    &__logo {
      display: flex;
      align-items: center;

      :slotted(img),
      :slotted(svg) {
        height: 36px;
      }
    }

    &__navigation {
      display: flex;
      gap: 28px;
      flex: 1;
      font-family: var(--bf-font-display);
      font-weight: var(--bf-fw-semibold);
      font-size: 15px;
    }

    &__navigation-link {
      color: var(--bf-black);
      text-decoration: none;
      transition: color var(--bf-dur-fast) var(--bf-ease);

      &:hover {
        color: var(--bf-orange-dark);
      }

      &[aria-current='page'] {
        color: var(--bf-orange-dark);
        border-bottom: 3px solid var(--bf-orange-light);
        padding-bottom: 4px;
      }
    }

    @media (max-width: 1024px) {
      height: var(--bf-header-h-tablet);
    }

    @media (max-width: 600px) {
      height: var(--bf-header-h-mobile);
      padding: 0 16px;
      gap: 16px;

      &__navigation {
        display: none;
      }
    }
  }
</style>
